// Data helpers for the Teams and Players pages.
//
// Reuses the existing match/session plumbing (dbutils + firestoreData) and adds
// two small Firestore-backed link collections so playlists and saved AI reports
// can be assigned to a team or a player:
//   - playlistAssignments/{owner}_{scope}_{refId}_{playlistId}
//   - savedReports/{owner}_{scope}_{refId}_{timestamp}
// where scope is "team" | "player" and refId is the team/player document id.

import axios from "axios";
import { getAuth } from "firebase/auth";
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    query,
    serverTimestamp,
    setDoc,
    where,
} from "firebase/firestore";
import { db } from "../../firebase.config";
import { fetchAllMyMatches, fetchSessionById } from "./dbutils";
import { decodeHtml, unzipBuffer } from "./Utils";
import { myunzip } from "./zip";

function currentUid() {
  return getAuth().currentUser?.uid ?? null;
}

function currentEmail() {
  return getAuth().currentUser?.email ?? null;
}

const PLAYLIST_ASSIGN = "playlistAssignments";
const SAVED_REPORTS = "savedReports";
const SAVED_FILES = "savedReportFiles";
const SHARED_REPORT_GRANTS = "sharedReportGrants";
const SHARED_REPORT_GRANT_FILES = "sharedReportGrantFiles";

// Normalise shareUsers to a clean array of lowercase email strings regardless
// of whether it arrives as a comma-separated string or an array.
function normalizeShareEmails(value) {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : String(value).split(",");
  return [
    ...new Set(raw.map((s) => String(s).trim().toLowerCase()).filter(Boolean)),
  ];
}

// Normalise an email for use as part of a Firestore document id.
function safeEmailId(email) {
  return String(email ?? "").toLowerCase().replace(/[^a-z0-9]/g, "_");
}

// ---- Matches --------------------------------------------------------------

// All of the user's matches (Firestore when migrated, else legacy API).
export async function fetchMyMatches() {
  return (await fetchAllMyMatches(currentEmail())) ?? [];
}

export function matchesForTeamName(allMatches, name) {
  const lower = String(name ?? "").trim().toLowerCase();
  if (!lower) return [];
  return (allMatches ?? []).filter(
    (m) =>
      String(m.teamA ?? "").toLowerCase() === lower ||
      String(m.teamB ?? "").toLowerCase() === lower
  );
}

export function matchesForPlayer(allMatches, player) {
  const names = (player?.teamNames ?? []).map((n) =>
    String(n ?? "").toLowerCase()
  );
  if (names.length === 0) return [];
  return (allMatches ?? []).filter(
    (m) =>
      names.includes(String(m.teamA ?? "").toLowerCase()) ||
      names.includes(String(m.teamB ?? "").toLowerCase())
  );
}

// Build the navigation state used by MatchesList.doMatch so a match opens in
// the Session page. Returns null when the session has no stats.
export async function loadSessionState(matchId) {
  const session = await fetchSessionById(matchId);
  if (!session || !session.stats) return null;
  let uz = myunzip(session.stats);
  if (!uz || uz.length === 0) {
    uz = unzipBuffer(session.stats);
  }
  if (!uz) return null;
  const isPSVB = uz.includes("PSVB");
  return {
    msession: session,
    dvwFileData: isPSVB ? null : uz,
    psvbFileData: isPSVB ? uz : null,
    filename: "",
  };
}

// Load full stats buffers for a set of match metadata objects, for the AI
// Reports page (mirrors MatchesList.loadSelectedMatchesForReports).
export async function loadMatchesWithBuffers(matches) {
  const xms = [];
  for (const m of matches ?? []) {
    const xm = await fetchSessionById(m.id);
    if (!xm || !xm.stats) continue;
    let buffer = myunzip(xm.stats);
    if (!buffer) {
      buffer = unzipBuffer(xm.stats);
    }
    if (!buffer) continue;
    xm.buffer = buffer;
    xms.push(xm);
  }
  return xms;
}

// ---- Playlists (legacy API) ----------------------------------------------

function apiUrl() {
  return process.env.REACT_APP_VBLIVE_API_URL;
}

export async function fetchAllMyPlaylists() {
  const email = currentEmail();
  if (!email || !apiUrl()) return [];
  const url = apiUrl().includes("vercel")
    ? `${apiUrl()}/Session/GetPlaylistInfoInServerForApp/${email}/VBLive`
    : `${apiUrl()}/Session/GetPlaylistInfoInServerForApp?serverName=${email}&appName=VBLive`;
  try {
    const res = await axios.get(url);
    const playlists = res.data ?? [];
    for (const pl of playlists) {
      pl.description = decodeHtml(pl.description);
      pl.comments = decodeHtml(pl.comments);
      pl.tags = decodeHtml(pl.tags);
    }
    return playlists;
  } catch (e) {
    console.log("Failed to load playlists", e);
    return [];
  }
}

// Navigation state for opening a playlist in the Playlist page.
export async function loadPlaylistState(playlistId) {
  if (!apiUrl()) return null;
  const url = apiUrl().includes("vercel")
    ? `${apiUrl()}/Session/GetPlaylistById/${playlistId}`
    : `${apiUrl()}/Session/GetPlaylistById?playlistId=${playlistId}`;
  try {
    const res = await axios.get(url);
    const pl = res.data?.[0];
    if (!pl) return null;
    pl.description = decodeHtml(pl.description);
    pl.comments = decodeHtml(pl.comments);
    pl.tags = decodeHtml(pl.tags);
    return { playlist: pl, serverName: currentEmail() };
  } catch (e) {
    console.log("Failed to load playlist", e);
    return null;
  }
}

// ---- Playlist assignments (Firestore) ------------------------------------

export async function fetchAssignedPlaylists(scope, refId) {
  const uid = currentUid();
  if (!uid || !refId) return [];
  const q = query(collection(db, PLAYLIST_ASSIGN), where("owner", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((a) => a.scope === scope && a.refId === refId)
    .sort((a, b) =>
      String(a.description ?? "").localeCompare(String(b.description ?? ""))
    );
}

export async function assignPlaylist(scope, refId, playlist) {
  const uid = currentUid();
  if (!uid || !refId || !playlist) return null;
  const id = `${uid}_${scope}_${refId}_${playlist.id}`;
  const data = {
    owner: uid,
    ownerEmail: currentEmail(),
    scope,
    refId,
    playlistId: playlist.id,
    description: playlist.description ?? "",
    tags: playlist.tags ?? "",
    dateInSeconds: playlist.dateInSeconds ?? null,
  };
  await setDoc(doc(db, PLAYLIST_ASSIGN, id), data);
  return { id, ...data };
}

export async function unassignPlaylist(assignmentId) {
  if (!assignmentId) return;
  await deleteDoc(doc(db, PLAYLIST_ASSIGN, assignmentId));
}

// Returns all playlistAssignment docs for a given playlist id, enriched with
// the team/player display name looked up from the teams/players collections.
export async function fetchAssignmentsForPlaylist(playlistId) {
  const uid = currentUid();
  if (!uid || !playlistId) return [];
  const q = query(collection(db, PLAYLIST_ASSIGN), where("owner", "==", uid));
  const snap = await getDocs(q);
  const assignments = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((a) => String(a.playlistId) === String(playlistId));

  const enriched = await Promise.all(
    assignments.map(async (a) => {
      try {
        const coll = a.scope === "team" ? "teams" : "players";
        const docSnap = await getDoc(doc(db, coll, a.refId));
        if (docSnap.exists()) {
          const data = docSnap.data();
          const refName =
            a.scope === "team"
              ? (data.name ?? a.refId)
              : `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim() ||
                data.nickName ||
                a.refId;
          return { ...a, refName };
        }
      } catch {}
      return { ...a, refName: a.refId };
    })
  );
  return enriched.sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === "team" ? -1 : 1;
    return String(a.refName ?? "").localeCompare(String(b.refName ?? ""));
  });
}

// ---- Saved AI reports (Firestore) ----------------------------------------

export async function fetchSavedReports(scope, refId) {
  const uid = currentUid();
  if (!uid || !refId) return [];
  const q = query(collection(db, SAVED_REPORTS), where("owner", "==", uid));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => r.scope === scope && r.refId === refId)
    .sort((a, b) => (b.createdAtSeconds ?? 0) - (a.createdAtSeconds ?? 0));
}

export async function saveReport(scope, refId, { title, kind, content }) {
  const uid = currentUid();
  if (!uid || !refId) return null;
  const id = `${uid}_${scope}_${refId}_${Date.now()}`;
  const data = {
    owner: uid,
    ownerEmail: currentEmail(),
    scope,
    refId,
    title: title?.trim() || "Report",
    kind: kind || "custom",
    content: content ?? "",
    shareStatus: 0,
    shareUsers: [],
    createdAtSeconds: Math.floor(Date.now() / 1000),
  };
  await setDoc(doc(db, SAVED_REPORTS, id), data);
  return { id, ...data };
}

// Save a generated PDF report. The (compressed) PDF is stored as base64. The
// lightweight metadata goes in savedReports while the heavy base64 payload is
// kept in a separate savedReportFiles doc, so listing reports stays cheap and
// the file is only fetched when a report is opened. Stored in Firestore (not
// Firebase Storage) to avoid Storage/CORS setup.
export async function saveReportPdf(scope, refId, { title, pdfBase64, sizeBytes }) {
  const uid = currentUid();
  if (!uid || !refId || !pdfBase64) return null;
  const id = `${uid}_${scope}_${refId}_${Date.now()}`;
  const data = {
    owner: uid,
    ownerEmail: currentEmail(),
    scope,
    refId,
    title: title?.trim() || "Report",
    kind: "pdf",
    sizeBytes: sizeBytes ?? 0,
    hasFile: true,
    content: "",
    shareStatus: 0,
    shareUsers: [],
    createdAtSeconds: Math.floor(Date.now() / 1000),
  };
  await setDoc(doc(db, SAVED_REPORTS, id), data);
  await setDoc(doc(db, SAVED_FILES, id), { owner: uid, pdfBase64 });
  return { id, ...data };
}

// Fetch the base64 PDF payload for a saved report (loaded on demand).
export async function fetchReportFile(id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, SAVED_FILES, id));
  return snap.exists() ? snap.data().pdfBase64 || null : null;
}

// Delete a saved report. Accepts either the report id (legacy) or the full
// report object; the backing PDF file doc is removed too.
export async function deleteSavedReport(report) {
  const id = typeof report === "string" ? report : report?.id;
  if (!id) return;
  await deleteDoc(doc(db, SAVED_REPORTS, id));
  try {
    await deleteDoc(doc(db, SAVED_FILES, id));
  } catch (e) {
    console.log("Failed to delete report file", e);
  }
}

// ---- Sharing saved AI reports (Firestore) --------------------------------
//
// Mirrors firestoreData.js's fsShareSession/sharedGrants pattern for matches:
// shareStatus 0 = not shared, 1 = available to everyone, 2 = available to
// selected users. For status 2 we write one lightweight "grant" doc per
// recipient (sharedReportGrants, queryable by recipientEmail — works without
// needing cross-user security rules on the owner's own savedReports/
// savedReportFiles docs) plus a matching heavy-payload doc
// (sharedReportGrantFiles) carrying the report's own content/PDF so a
// recipient never needs read access to the owner's docs at all.
export async function shareReport(report, { shareStatus, shareUsers }) {
  const uid = currentUid();
  if (!uid || !report?.id) return false;
  const recipients = normalizeShareEmails(shareUsers);

  try {
    await setDoc(
      doc(db, SAVED_REPORTS, report.id),
      { shareStatus: shareStatus ?? 0, shareUsers: recipients },
      { merge: true }
    );

    // Remove any previously-written grants for this report before writing
    // the current recipient list.
    const existingSnap = await getDocs(
      query(
        collection(db, SHARED_REPORT_GRANTS),
        where("reportId", "==", report.id),
        where("ownerUid", "==", uid)
      )
    );
    for (const grantDoc of existingSnap.docs) {
      await deleteDoc(grantDoc.ref);
      try {
        await deleteDoc(doc(db, SHARED_REPORT_GRANT_FILES, grantDoc.id));
      } catch (e) {
        console.warn("shareReport: could not clean up old grant file:", e);
      }
    }

    if (shareStatus === 2 && recipients.length > 0) {
      let pdfBase64 = null;
      if (report.kind === "pdf") {
        pdfBase64 = await fetchReportFile(report.id);
      }
      for (const email of recipients) {
        const grantId = `${report.id}_${safeEmailId(email)}`;
        await setDoc(doc(db, SHARED_REPORT_GRANTS, grantId), {
          reportId: report.id,
          recipientEmail: email,
          ownerUid: uid,
          ownerEmail: currentEmail() ?? "",
          scope: report.scope ?? null,
          refId: report.refId ?? null,
          title: report.title ?? "Report",
          kind: report.kind ?? "custom",
          createdAtSeconds:
            report.createdAtSeconds ?? Math.floor(Date.now() / 1000),
          sharedAt: serverTimestamp(),
        });
        await setDoc(doc(db, SHARED_REPORT_GRANT_FILES, grantId), {
          content: report.content ?? "",
          pdfBase64: pdfBase64 ?? null,
        });
      }
    }

    return true;
  } catch (e) {
    console.error("shareReport failed:", e);
    return false;
  }
}

// Reports other users have shared directly with the signed-in user.
export async function fetchReportsSharedWithMe() {
  const email = currentEmail();
  if (!email) return [];
  try {
    const q = query(
      collection(db, SHARED_REPORT_GRANTS),
      where("recipientEmail", "==", email.toLowerCase())
    );
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.createdAtSeconds ?? 0) - (a.createdAtSeconds ?? 0));
  } catch (e) {
    console.warn("fetchReportsSharedWithMe failed:", e);
    return [];
  }
}

// Fetch the heavy payload (markdown content and/or base64 PDF) for a report
// shared with the signed-in user, loaded on demand when they open it.
export async function fetchSharedReportFile(grantId) {
  if (!grantId) return null;
  const snap = await getDoc(doc(db, SHARED_REPORT_GRANT_FILES, grantId));
  return snap.exists() ? snap.data() : null;
}
